import { A11yModule } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, HostListener, inject } from '@angular/core';
import { AlertTriangle, HelpCircle, LucideAngularModule, LucideIconData } from 'lucide-angular';

import { ConfirmDialogService } from './confirm-dialog.service';

/** Host único das confirmações. Montado no App; o conteúdo vem do ConfirmDialogService. */
@Component({
  selector: 'app-confirm-dialog',
  imports: [A11yModule, LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (dialog.active(); as d) {
      <div class="confirm-backdrop" (click)="dialog.settle(false)" aria-hidden="true"></div>
      <div
        class="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        [attr.aria-describedby]="d.message ? 'confirm-dialog-message' : null"
        cdkTrapFocus
        [cdkTrapFocusAutoCapture]="true">
        <div class="confirm-icon" [class.danger]="d.tone === 'danger'" aria-hidden="true">
          <lucide-icon [img]="d.tone === 'danger' ? dangerIcon : questionIcon" [size]="20"></lucide-icon>
        </div>
        <div class="confirm-copy">
          <h2 id="confirm-dialog-title">{{ d.title }}</h2>
          @if (d.message) {
            <p id="confirm-dialog-message">{{ d.message }}</p>
          }
        </div>
        <div class="confirm-actions">
          @if (d.tone === 'danger') {
            <button type="button" class="btn btn-secondary" cdkFocusInitial (click)="dialog.settle(false)">
              {{ d.cancelLabel }}
            </button>
            <button type="button" class="btn btn-danger-solid" (click)="dialog.settle(true)">
              {{ d.confirmLabel }}
            </button>
          } @else {
            <button type="button" class="btn btn-secondary" (click)="dialog.settle(false)">
              {{ d.cancelLabel }}
            </button>
            <button type="button" class="btn btn-primary" cdkFocusInitial (click)="dialog.settle(true)">
              {{ d.confirmLabel }}
            </button>
          }
        </div>
      </div>
    }
  `,
  styles: `
    .confirm-backdrop {
      position: fixed;
      inset: 0;
      z-index: 300;
      background: rgba(8, 14, 26, 0.5);
      animation: confirm-fade 140ms ease;
    }

    .confirm-dialog {
      position: fixed;
      z-index: 301;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: min(440px, calc(100vw - 2rem));
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 0.9rem 1rem;
      padding: 1.25rem;
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-lg);
      animation: confirm-in 160ms ease;
    }

    .confirm-icon {
      width: 40px;
      height: 40px;
      display: grid;
      place-items: center;
      border-radius: 50%;
      background: var(--color-primary-subtle);
      color: var(--color-primary);
    }

    .confirm-icon.danger {
      background: var(--color-danger-bg);
      color: var(--color-danger-fg);
    }

    .confirm-copy { min-width: 0; padding-top: 0.15rem; }

    .confirm-copy h2 {
      margin: 0;
      font-family: var(--font-display);
      font-size: 1.1875rem;
      font-weight: 600;
      color: var(--color-text-primary);
    }

    .confirm-copy p {
      margin: 0.35rem 0 0;
      font-size: 0.875rem;
      line-height: 1.5;
      color: var(--color-text-secondary);
    }

    .confirm-actions {
      grid-column: 1 / -1;
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    @media (max-width: 479px) {
      .confirm-dialog {
        top: auto;
        bottom: 0;
        transform: translateX(-50%);
        width: 100vw;
        border-radius: var(--radius-lg) var(--radius-lg) 0 0;
        padding-bottom: calc(1.25rem + env(safe-area-inset-bottom, 0px));
        animation: confirm-up 180ms ease;
      }

      .confirm-actions { flex-direction: column-reverse; }
      .confirm-actions .btn { width: 100%; }
    }

    @keyframes confirm-fade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes confirm-in {
      from { opacity: 0; transform: translate(-50%, calc(-50% + 6px)); }
      to { opacity: 1; transform: translate(-50%, -50%); }
    }
    @keyframes confirm-up {
      from { transform: translate(-50%, 100%); }
      to { transform: translate(-50%, 0); }
    }

    @media (prefers-reduced-motion: reduce) {
      .confirm-backdrop, .confirm-dialog { animation: none; }
    }
  `,
})
export class ConfirmDialogComponent {
  protected readonly dialog = inject(ConfirmDialogService);

  readonly dangerIcon: LucideIconData = AlertTriangle;
  readonly questionIcon: LucideIconData = HelpCircle;

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.dialog.active()) {
      this.dialog.settle(false);
    }
  }
}
